<template>
  <Layout>
    <div class="ft">
      <div class="ft__head">
        <h4 class="ft__title mb-0">Трекер задач</h4>
        <button type="button" class="btn btn-outline-secondary btn-sm ft__refresh" :disabled="refreshing" @click="refresh">
          <span v-if="refreshing" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Обновить
        </button>
      </div>

      <div class="ft__tabs" role="tablist">
        <button
          v-for="t in tabs"
          :key="t.key"
          type="button"
          role="tab"
          class="ft__tab"
          :class="{ 'ft__tab--active': tab === t.key }"
          :aria-selected="tab === t.key ? 'true' : 'false'"
          @click="selectTab(t.key)"
        >
          <span class="ft__tab-long">{{ t.long }}</span><span class="ft__tab-short">{{ t.short }}</span>
          <span v-if="t.count !== null" class="ft__tab-count">{{ t.count }}</span>
        </button>
      </div>

      <template v-if="tab === 'active'">
        <ConsumablesBlock :rows="consumableRows" :status="consumables.status" :error="consumables.error" @retry="reloadConsumables" />
        <div v-if="tasks.status === 'error'" class="alert alert-danger py-2 px-3 mb-0 d-flex align-items-center ft__banner" role="alert">
          <span>Не удалось загрузить задачи: {{ errorMessage(tasks.error) }}</span>
          <button type="button" class="btn btn-sm btn-light ml-auto ft__retry" @click="reloadTasks">Повторить</button>
        </div>
        <div v-if="!activeBooted" class="ft__loading">
          <div class="spinner-border text-primary" role="status"><span class="sr-only">Загрузка…</span></div>
        </div>
        <div v-else-if="!activeCards.length && tasks.status !== 'error'" class="ft__empty">{{ activeEmpty }}</div>
        <div v-else-if="activeCards.length" class="ft__list">
          <FieldTaskCard v-for="card in activeCards" :key="card.id" :card="card" :disabled="!!modalCard" @complete="openComplete" />
        </div>
      </template>

      <template v-else>
        <div class="ft__period">
          <label class="ft__period-item">
            <span class="ft__period-label">с</span>
            <date-picker
              v-model="period.from"
              type="date"
              format="DD.MM.YYYY"
              value-type="YYYY-MM-DD"
              :clearable="false"
              :editable="false"
              :disabled-date="disableAfterTo"
              input-class="form-control ft__from"
              placeholder="дд.мм.гггг"
              @input="onPeriodChanged"
            />
          </label>
          <label class="ft__period-item">
            <span class="ft__period-label">по</span>
            <date-picker
              v-model="period.to"
              type="date"
              format="DD.MM.YYYY"
              value-type="YYYY-MM-DD"
              :clearable="false"
              :editable="false"
              :disabled-date="disableBeforeFrom"
              input-class="form-control ft__to"
              placeholder="дд.мм.гггг"
              @input="onPeriodChanged"
            />
          </label>
        </div>
        <div v-if="completed.status === 'error'" class="alert alert-danger py-2 px-3 mb-0 d-flex align-items-center ft__banner" role="alert">
          <span>Не удалось загрузить задачи: {{ errorMessage(completed.error) }}</span>
          <button type="button" class="btn btn-sm btn-light ml-auto ft__retry" @click="loadCompleted">Повторить</button>
        </div>
        <div v-else-if="completed.status !== 'ready'" class="ft__loading">
          <div class="spinner-border text-primary" role="status"><span class="sr-only">Загрузка…</span></div>
        </div>
        <div v-else-if="!completedCards.length" class="ft__empty">{{ doneEmpty }}</div>
        <div v-else class="ft__list">
          <CompletedTaskCard v-for="card in completedCards" :key="card.id" :card="card" />
        </div>
      </template>

      <CompleteTaskModal
        v-if="modalCard"
        :card="modalCard"
        @works-changed="onWorksChanged"
        @completed="onCompleted"
        @closed="onModalClosed"
      />
    </div>
  </Layout>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import Layout from '@/views/layouts/main';
import ConsumablesBlock from '@/components/field/ConsumablesBlock.vue';
import FieldTaskCard from '@/components/field/FieldTaskCard.vue';
import CompletedTaskCard from '@/components/field/CompletedTaskCard.vue';
import CompleteTaskModal from '@/components/field/CompleteTaskModal.vue';
import * as api from '@/components/management/dispatcher/api';
import {
  createResource, setLoading, setReady, setError, isReady, errorMessage,
} from '@/components/management/dispatcher/resource';
import { todayIso } from '@/components/management/dispatcher/format';
import { readPeriod, isValidPeriod, samePeriod } from '@/components/management/dispatcher/period';
import { notifySuccess } from '@/components/management/dispatcher/notify';
import {
  TABS, TAB_LABELS, ACTIVE_EMPTY, DONE_EMPTY, readTab, defaultPeriod, activeCardModel, completedCardModel,
  sortActive, sortCompleted, consumableRows,
} from '@/components/field/fieldTasksLogic';

/**
 * «Трекер задач» for field engineers (KOP-40 spec §6): active tasks + consumables pick-list,
 * the completion modal, and the completed tasks of a period mirrored in `?tab=done&from&to`.
 */
export default {
  name: 'FieldTasksPage',
  components: { Layout, DatePicker, ConsumablesBlock, FieldTaskCard, CompletedTaskCard, CompleteTaskModal },
  page: { title: 'Трекер задач' },
  data() {
    const query = this.$route && this.$route.query;
    return {
      tab: readTab(query),
      period: readPeriod(query, defaultPeriod(todayIso())),
      tasks: createResource(),
      consumables: createResource(),
      completed: createResource(),
      activeBooted: false,
      modalCard: null,
      worksChanged: false,
    };
  },
  computed: {
    tabs() {
      return [
        { key: TABS.ACTIVE, ...TAB_LABELS.active, count: isReady(this.tasks) ? this.activeCards.length : null },
        { key: TABS.DONE, ...TAB_LABELS.done, count: isReady(this.completed) ? this.completedCards.length : null },
      ];
    },
    activeCards() { return sortActive(this.tasks.data.map((t) => activeCardModel(t, todayIso()))); },
    completedCards() { return sortCompleted(this.completed.data.map(completedCardModel)); },
    consumableRows() { return consumableRows(this.consumables.data); },
    refreshing() {
      if (this.tab === TABS.DONE) return this.completed.status === 'loading';
      return this.tasks.status === 'loading' || this.consumables.status === 'loading';
    },
    activeEmpty() { return ACTIVE_EMPTY; },
    doneEmpty() { return DONE_EMPTY; },
  },
  watch: {
    '$route.query': function onQuery(query) { this.applyQuery(query); },
  },
  created() {
    this.seq = {}; // non-reactive: latest request id per resource key
    this.requested = null; // non-reactive: period of the latest completed-list request
    this.loadActive();
    if (this.tab === TABS.DONE) this.loadCompleted();
  },
  methods: {
    errorMessage,
    /* ---------- loading (latest request wins, resource mutated in place) ---------- */
    async loadLatest(key, fetcher) {
      const r = this[key];
      this.seq[key] = (this.seq[key] || 0) + 1;
      const mine = this.seq[key];
      setLoading(r);
      try {
        const data = await fetcher();
        if (mine !== this.seq[key]) return false;
        setReady(r, data);
        return true;
      } catch (e) {
        if (mine !== this.seq[key]) return false;
        setError(r, e);
        return false;
      }
    },
    reloadTasks() { return this.loadLatest('tasks', api.getActiveTasks); },
    reloadConsumables() { return this.loadLatest('consumables', api.getAssignedConsumables); },
    async loadActive() {
      await Promise.all([this.reloadTasks(), this.reloadConsumables()]);
      this.activeBooted = true;
    },
    loadCompleted() {
      const period = { from: this.period.from, to: this.period.to };
      this.requested = period;
      return this.loadLatest('completed', () => api.getCompletedTasks({ completed_from: period.from, completed_to: period.to }));
    },
    refresh() {
      if (this.refreshing) return;
      if (this.tab === TABS.DONE) this.loadCompleted();
      else this.loadActive();
    },
    /* ---------- tabs, period, URL ---------- */
    selectTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.syncQuery();
      if (tab === TABS.DONE && !samePeriod(this.requested, this.period)) this.loadCompleted();
    },
    /** Fires after v-model has already updated `period` (Vue runs the model callback first). */
    onPeriodChanged() {
      if (!isValidPeriod(this.period)) return;
      this.syncQuery();
      this.loadCompleted();
    },
    /** Boot/history: re-reads tab and period; our own replace() lands here too and must be a no-op. */
    applyQuery(query) {
      const tab = readTab(query);
      this.tab = tab;
      // The active tab's URL carries no period: keep the one already chosen for the done tab.
      if (tab !== TABS.DONE) return;
      const period = readPeriod(query, defaultPeriod(todayIso()));
      if (!samePeriod(period, this.period)) this.period = { from: period.from, to: period.to };
      if (!samePeriod(this.requested, this.period)) this.loadCompleted();
    },
    syncQuery() {
      const current = (this.$route && this.$route.query) || {};
      const next = this.tab === TABS.DONE ? { tab: TABS.DONE, from: this.period.from, to: this.period.to } : {};
      if (current.tab === next.tab && current.from === next.from && current.to === next.to) return;
      // vue-router 3.5 rejects replace() on a duplicated navigation; nothing to report.
      this.$router.replace({ query: next }).catch(() => {});
    },
    disableAfterTo(date) { return todayIso(date) > this.period.to; },
    disableBeforeFrom(date) { return todayIso(date) < this.period.from; },
    /* ---------- completion modal ---------- */
    openComplete(card) {
      if (this.modalCard) return;
      this.worksChanged = false;
      this.modalCard = card;
    },
    onWorksChanged() { this.worksChanged = true; },
    onModalClosed() {
      const changed = this.worksChanged;
      this.worksChanged = false;
      this.modalCard = null;
      if (changed) this.reloadTasks();
    },
    onCompleted(card) {
      notifySuccess(this, `Задача ${card.label} выполнена`);
      this.worksChanged = false;
      this.modalCard = null;
      this.loadActive();
      const today = todayIso();
      if (this.requested && this.requested.from <= today && today <= this.requested.to) this.loadCompleted();
    },
  },
};
</script>

<style scoped lang="scss">
.ft { max-width: 640px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
.ft__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.ft__title { font-size: 18px; font-weight: 700; color: #1d221f; }
.ft__refresh { min-height: 44px; display: inline-flex; align-items: center; }
.ft__tabs { display: flex; background: #fff; border: 1px solid #e3e5e1; border-radius: 12px; padding: 4px; gap: 4px; }
.ft__tab { flex: 1; min-height: 44px; border: 0; border-radius: 9px; background: transparent; font-weight: 700; font-size: 14px; color: #4c524d; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 0 8px; }
.ft__tab--active { background: #1d221f; color: #fff; }
.ft__tab-count { font-size: 12px; font-weight: 600; opacity: .7; }
.ft__tab-short { display: none; }
.ft__period { display: flex; gap: 12px; background: #fff; border: 1px solid #e3e5e1; border-radius: 12px; padding: 10px 12px; }
.ft__period-item { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; margin: 0; }
.ft__period-label { color: #8b918c; font-size: 13px; }
.ft__list { display: flex; flex-direction: column; gap: 12px; }
.ft__loading { display: flex; justify-content: center; padding: 32px 0; }
.ft__empty { text-align: center; color: #8b918c; padding: 32px 0; }
@media (max-width: 575.98px) {
  .ft__tab-long { display: none; }
  .ft__tab-short { display: inline; }
  .ft__period { flex-direction: column; gap: 8px; }
}
</style>
